/* eslint-disable react/no-unknown-property */

import * as React from 'react'
import { useThree, ThreeEvent } from '@react-three/fiber'
import { CanvasTexture } from 'three'
import { useGizmoContext } from '@react-three/drei'

type AxisProps = {
  color: string
  rotation: [number, number, number]
  opacity?: number
}

type AxisHeadProps = JSX.IntrinsicElements['sprite'] & {
  arcStyle: string
  disabled?: boolean
  onClick?: (e: ThreeEvent<MouseEvent>) => null
  opacity?: number
}

type GizmoViewportProps = JSX.IntrinsicElements['group'] & {
  disabled?: boolean
  onClick?: (e: ThreeEvent<MouseEvent>) => null
}

function Axis ({ opacity = 1, color, rotation }: AxisProps) {
  return (
    <group rotation={rotation}>
      <mesh position={[0.3, 0, 0]}>
        <boxGeometry args={[0.6, 0.03, 0.03]} />
        <meshBasicMaterial
          color={color}
          toneMapped={false}
          opacity={opacity}
          transparent
        />
      </mesh>
    </group>
  )
}

function AxisHead ({
  onClick,
  disabled,
  arcStyle,
  opacity = 1,
  ...props
}: AxisHeadProps) {
  const gl = useThree((state) => state.gl)
  const texture = React.useMemo(() => {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 64

    const context = canvas.getContext('2d')!
    context.beginPath()
    context.arc(32, 32, 15, 0, 2 * Math.PI)
    context.closePath()
    context.fillStyle = arcStyle
    context.fill()

    return new CanvasTexture(canvas)
  }, [arcStyle])

  const [active, setActive] = React.useState(false)
  const scale = (0.75) * (active ? 1.2 : 1)
  const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    setActive(true)
  }
  const handlePointerOut = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    setActive(false)
  }
  return (
    <sprite
      scale={scale}
      onPointerOver={!disabled ? handlePointerOver : undefined}
      onPointerOut={!disabled ? onClick || handlePointerOut : undefined}
      {...props}
    >
      <spriteMaterial
        map={texture}
        map-anisotropy={gl.capabilities.getMaxAnisotropy() || 1}
        opacity={opacity}
        toneMapped={false}
      />
    </sprite>
  )
}

const Gizmo = ({
  disabled,
  onClick,
  ...props
}: GizmoViewportProps) => {
  const { tweenCamera } = useGizmoContext()
  const axisHeadProps = {
    disabled,
    onClick,
    onPointerDown: !disabled
      ? (e: ThreeEvent<PointerEvent>) => {
        tweenCamera(e.object.position)
        e.stopPropagation()
      }
      : undefined,
  }
  return (
    <group scale={30} {...props}>
      <Axis
        color="#FB415C"
        rotation={[0, 0, 0]}
      />
      <Axis
        color="#44524F"
        opacity={0.3}
        rotation={[0, Math.PI, 0]}
      />
      <Axis
        color="#099B78"
        rotation={[0, 0, Math.PI / 2]}
      />
      <Axis
        color="#44524F"
        opacity={0.3}
        rotation={[Math.PI, 0, Math.PI / 2]}
      />
      <Axis
        color="#2388FA"
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Axis
        color="#44524F"
        opacity={0.3}
        rotation={[0, -Math.PI / 2, Math.PI]}
      />
      <AxisHead
        arcStyle="#FB415C"
        position={[0.8, 0, 0]}
        {...axisHeadProps}
      />
      <AxisHead
        arcStyle="#099B78"
        position={[0, 0.8, 0]}
        {...axisHeadProps}
      />
      <AxisHead
        arcStyle="#2388FA"
        position={[0, 0, 0.8]}
        {...axisHeadProps}
      />
      <AxisHead
        arcStyle="#44524F"
        opacity={0.3}
        position={[-0.8, 0, 0]}
        {...axisHeadProps}
      />
      <AxisHead
        arcStyle="#44524F"
        opacity={0.3}
        position={[0, -0.8, 0]}
        {...axisHeadProps}
      />
      <AxisHead
        arcStyle="#44524F"
        opacity={0.3}
        position={[0, 0, -0.8]}
        {...axisHeadProps}
      />
    </group>
  )
}

export default Gizmo
